import { useState, useCallback } from 'react';
import {
  BULK_DELETE_LIMIT,
  BULK_DELETE_LIMIT_MESSAGE,
  toggleStudentInSelection,
  selectStudentsUpToLimit,
  isAllSelectableSelected,
  isSelectionAtLimit
} from '../utils/bulkStudentSelection';

/**
 * Shared bulk selection state for student delete flows (max 100 at a time).
 */
export function useBulkStudentSelection(limit = BULK_DELETE_LIMIT) {
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectionWarning, setSelectionWarning] = useState('');

  const clearSelection = useCallback(() => {
    setSelectedStudents([]);
    setSelectionWarning('');
  }, []);

  const toggleStudentSelection = useCallback((student) => {
    if (student == null) {
      clearSelection();
      return;
    }

    setSelectedStudents((prev) => {
      const { selection, limitReached } = toggleStudentInSelection(prev, student, limit);
      setSelectionWarning(limitReached ? BULK_DELETE_LIMIT_MESSAGE : '');
      return selection;
    });
  }, [limit, clearSelection]);

  const toggleAllSelection = useCallback((candidates) => {
    setSelectedStudents((prev) => {
      if (isAllSelectableSelected(prev, candidates, limit)) {
        setSelectionWarning('');
        return [];
      }
      const next = selectStudentsUpToLimit(candidates, limit);
      if (candidates.length > limit) {
        setSelectionWarning(
          `Only the first ${limit} students were selected. ${BULK_DELETE_LIMIT_MESSAGE}`
        );
      } else {
        setSelectionWarning('');
      }
      return next;
    });
  }, [limit]);

  return {
    selectedStudents,
    setSelectedStudents,
    selectionWarning,
    setSelectionWarning,
    clearSelection,
    toggleStudentSelection,
    toggleAllSelection,
    selectionAtLimit: isSelectionAtLimit(selectedStudents.length, limit),
    selectionLimit: limit
  };
}

export default useBulkStudentSelection;
